import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IDocumentMediaRemark,
  IDocumentRedaction,
} from '../../../../api/types/document.type';
import {isEmpty} from '../../../../utils/formatters';
import {durationToSeconds} from '../../../../utils/utils';
import ArrWaveform from '../../../common/arr-waveform/waveform/arrWaveform';
import RegionsPlugin, {
  Region,
  RegionParams,
} from '../../../common/arr-waveform/waveform/plugins/regions';
import './ArrAVPreviewProvider.scss';

interface ArrAVPreviewContextType {
  isPlayerReady: boolean;
  isVideoPlayerReady: boolean;
  initVolume: number;
  setIsPlayerReady: (isReady: boolean) => void;
  setIsVideoPlayerReady: (isReady: boolean) => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
  setTime: (seconds: number) => void;
  setVolume: (value: number) => void;
  setPlaybackRate: (value: number) => void;
  setArrWaveformRef: (arrwaveformInstance: ArrWaveform) => void;
  addRegion: (id: string, regionParams: RegionParams, color?: string) => void;
  deleteRegion: (id: string) => void;
  playRegion: (id: string) => void;
  stopRegion: (id: string) => void;
  editRegion: (id: string, regionParams: RegionParams) => void;
  getRegions: () => Region[];
  addMarkers: (remarks: IDocumentMediaRemark[]) => void;
  deleteAllMarkers: () => void;
  addMarker: (id: string, seconds: number, color?: string) => void;
  removeMarker: (id: string) => void;
  editMarker: (id: string, seconds: number) => void;
  playMarker: (id: string) => void;
  clearAllRegionsMarkers: () => void;
  getVolume: () => number;
  clearAllRegions: () => void;
  addRegionsFromRedactions: (redactions: IDocumentRedaction[]) => void;
}

const DEFAULT_VOLUME = 80;

export const ArrAVPreviewContext = createContext<ArrAVPreviewContextType>({
  isPlayerReady: false,
  isVideoPlayerReady: false,
  initVolume: DEFAULT_VOLUME,
  setIsPlayerReady: () => {},
  setIsVideoPlayerReady: () => {},
  play: () => {},
  pause: () => {},
  stop: () => {},
  setTime: () => {},
  setVolume: () => {},
  setPlaybackRate: () => {},
  setArrWaveformRef: () => {},
  addRegion: () => {},
  deleteRegion: () => {},
  playRegion: () => {},
  stopRegion: () => {},
  editRegion: () => {},
  getRegions: () => [],
  addMarkers: () => {},
  deleteAllMarkers: () => {},
  addMarker: () => {},
  removeMarker: () => {},
  editMarker: () => {},
  playMarker: () => {},
  clearAllRegionsMarkers: () => {},
  addRegionsFromRedactions: () => {},
  clearAllRegions: () => {},
  getVolume: () => 0,
});

interface ArrAVPreviewProviderProps {
  defaultVolume?: number;
}

export const ArrAVPreviewProvider: React.FC<
  React.PropsWithChildren<ArrAVPreviewProviderProps>
> = ({defaultVolume = DEFAULT_VOLUME, children}) => {
  const arrwaveform = useRef<ArrWaveform | null>(null);

  const [isPlayerReady, setIsPlayerReady] = useState<boolean>(false);
  const [isVideoPlayerReady, setIsVideoPlayerReady] = useState<boolean>(false);
  const [initVolume, setInitVolume] = useState<number>(defaultVolume);

  useEffect(() => {
    setInitVolume(defaultVolume);
  }, [defaultVolume]);

  useEffect(() => {
    if (arrwaveform.current && isPlayerReady) {
      arrwaveform.current.setVolume(initVolume / 100);
    }
  }, [initVolume, isPlayerReady]);

  const getRegionsPlugin = useCallback(
    () => arrwaveform.current?.options?.plugins?.[0] as RegionsPlugin,
    []
  );

  const play = useCallback(() => {
    arrwaveform.current?.play();
  }, []);

  const pause = useCallback(() => {
    arrwaveform.current?.pause();
  }, []);

  const stop = useCallback(() => {
    arrwaveform.current?.stop();
  }, []);

  const setVolume = useCallback((value: number) => {
    arrwaveform.current?.setVolume(value);
  }, []);

  const getVolume = useCallback(() => {
    return arrwaveform.current?.getVolume() ?? 0;
  }, []);

  const setPlaybackRate = useCallback((value: number) => {
    arrwaveform.current?.setPlaybackRate(value);
  }, []);

  const setTime = useCallback((seconds: number) => {
    arrwaveform.current?.setTime(seconds);
    arrwaveform.current?.pause();
  }, []);

  const setArrWaveformRef = useCallback((arrwaveformInstance: ArrWaveform) => {
    if (!arrwaveformInstance) return;
    arrwaveform.current = arrwaveformInstance;
  }, []);

  const addRegion = useCallback(
    (id: string, regionParams: RegionParams, color?: string) => {
      getRegionsPlugin()?.addRegion({
        ...regionParams,
        id,
        color: regionParams?.color ?? color ?? 'var(--region-color)',
        drag: false,
        resize: false,
      });
    },
    [getRegionsPlugin]
  );

  const addRegionsFromRedactions = useCallback(
    (redactions: IDocumentRedaction[]) => {
      redactions.forEach(({documentAudioRedactionID, startTime, endTime}) => {
        addRegion(documentAudioRedactionID.toString(), {
          start: durationToSeconds(startTime),
          end: durationToSeconds(endTime),
        });
      });
    },
    [addRegion]
  );

  const deleteRegion = useCallback(
    (id: string) => {
      getRegionsPlugin()
        ?.getRegions?.()
        ?.find((r) => r.id === id)
        ?.remove();
    },
    [getRegionsPlugin]
  );

  const editRegion = useCallback(
    (id: string, regionParams: RegionParams) => {
      const region = {
        ...(getRegionsPlugin()
          ?.getRegions?.()
          ?.find((r) => r.id === id) ?? {}),
      };

      deleteRegion(id);
      addRegion(id, {
        ...region,
        ...regionParams,
      });
    },
    [addRegion, deleteRegion, getRegionsPlugin]
  );

  const playRegion = useCallback(
    (id: string) => {
      getRegionsPlugin()
        ?.getRegions?.()
        ?.find((r) => r.id === id)
        ?.play(true);
    },
    [getRegionsPlugin]
  );

  const stopRegion = useCallback(
    (id: string) => {
      const region = getRegionsPlugin()
        ?.getRegions?.()
        ?.find((r) => r.id === id);
      arrwaveform.current?.setTime(region?.start ?? 0);
      arrwaveform.current?.pause();
    },
    [getRegionsPlugin]
  );

  const getRegions = useCallback(() => {
    return getRegionsPlugin()?.getRegions?.() ?? [];
  }, [getRegionsPlugin]);

  const deleteAllMarkers = useCallback(() => {
    const regions: Region[] =
      getRegionsPlugin()
        ?.getRegions?.()
        ?.filter((r) => r.start === r.end || isEmpty(r.end)) ?? [];

    for (const region of regions) {
      region.remove();
    }
  }, [getRegionsPlugin]);

  const addMarker = useCallback(
    (id: string, seconds: number, color?: string) => {
      addRegion(`remark-${id}`, {start: seconds}, color);
    },
    [addRegion]
  );

  const addMarkers = useCallback(
    (remarks: IDocumentMediaRemark[]) => {
      deleteAllMarkers();
      for (const remark of remarks) {
        addMarker(
          `${remark.documentAudioRemarkID}`,
          durationToSeconds(remark.startTime),
          remark.remarkColor
        );
      }
    },
    [addMarker, deleteAllMarkers]
  );

  const removeMarker = useCallback(
    (id: string) => {
      deleteRegion(`remark-${id}`);
    },
    [deleteRegion]
  );

  const playMarker = useCallback(
    (id: string) => {
      playRegion(`remark-${id}`);
    },
    [playRegion]
  );

  const editMarker = useCallback(
    (id: string, seconds: number) => {
      editRegion(`remark-${id}`, {start: seconds});
    },
    [editRegion]
  );

  const clearAllRegions = useCallback(() => {
    const regions: Region[] =
      getRegionsPlugin()
        ?.getRegions?.()
        ?.filter(
          (r) => r.start !== r.end && !isEmpty(r.start) && !isEmpty(r.end)
        ) ?? [];

    regions.forEach((region: Region) => {
      region.remove();
    });
  }, [getRegionsPlugin]);

  const clearAllRegionsMarkers = useCallback(() => {
    getRegionsPlugin()?.clearRegions();
  }, [getRegionsPlugin]);

  const values = useMemo(() => {
    return {
      isPlayerReady,
      setIsPlayerReady,
      isVideoPlayerReady,
      setIsVideoPlayerReady,
      initVolume,
      play,
      pause,
      stop,
      setArrWaveformRef,
      addRegion,
      deleteRegion,
      playRegion,
      stopRegion,
      editRegion,
      getRegions,
      setTime,
      addMarkers,
      deleteAllMarkers,
      addMarker,
      removeMarker,
      playMarker,
      editMarker,
      getVolume,
      setVolume,
      setPlaybackRate,
      clearAllRegionsMarkers,
      addRegionsFromRedactions,
      clearAllRegions,
    };
  }, [
    isPlayerReady,
    isVideoPlayerReady,
    initVolume,
    play,
    pause,
    stop,
    setArrWaveformRef,
    addRegion,
    deleteRegion,
    playRegion,
    stopRegion,
    editRegion,
    getRegions,
    setTime,
    addMarkers,
    deleteAllMarkers,
    addMarker,
    removeMarker,
    playMarker,
    editMarker,
    getVolume,
    setVolume,
    setPlaybackRate,
    clearAllRegionsMarkers,
    addRegionsFromRedactions,
    clearAllRegions,
  ]);

  return (
    <ArrAVPreviewContext.Provider value={values}>
      {children}
    </ArrAVPreviewContext.Provider>
  );
};
